import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { useTheme } from "@/contexts/ThemeContext";
import api from "@/lib/api";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try { await api.post("/auth/forgot-password", { email }); } catch (_e) {}
    setSubmitting(false);
    setSent(true);
  };

  return (
    <div className={["min-h-screen flex items-center justify-center px-4", theme === "dark" ? "app-bg-dark" : "app-bg-light"].join(" ")}>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="glass-elevated p-8 sm:p-10 w-full max-w-md"
        data-testid="forgot-card"
      >
        <div className="flex flex-col items-center mb-6">
          <Logo size={56} />
          <h1 className="mt-4 font-heading text-2xl font-extrabold tracking-[0.18em] brand-gradient-text">
            SHARDA ENERGY OS
          </h1>
        </div>
        <div className="mb-5">
          <h2 className="font-heading text-xl">{t("forgot.title")}</h2>
          <p className="text-sm text-muted-foreground mt-1">{t("forgot.subtitle")}</p>
        </div>
        {sent ? (
          <div data-testid="forgot-sent" className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 text-emerald-200 px-4 py-3 text-sm">
            {t("forgot.sent")}
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
            <div>
              <Label htmlFor="fp-email" className="text-xs uppercase tracking-widest">{t("login.email")}</Label>
              <Input
                id="fp-email"
                data-testid="forgot-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 bg-white/5 border-[var(--surface-border)] h-11"
              />
            </div>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="forgot-submit-button"
              className="w-full h-11 bg-gradient-primary text-white font-heading tracking-widest"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("forgot.submit")}
            </Button>
          </form>
        )}
        <Link
          to="/login"
          data-testid="forgot-back-link"
          className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-sky"
        >
          <ArrowLeft className="h-4 w-4" />
          {t("forgot.back")}
        </Link>
      </motion.div>
    </div>
  );
}
